import { useLogoutUserMutation } from "@/shared/graphql/queries/userQueries";
import { faCog, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { Styles } from "@/shared/coralreef";
import { useOnClickOutside } from "@/shared/coralreef/hooks/useOnClickOutside";
import { px, rem } from "csx";
import { Content, Horizontal } from "gls/lib";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "shared/hooks/useUser";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import { style } from "typestyle";
import { SideMenuOptions } from "./Sidebar";
import { SidebarItem } from "./SidebarItem";
interface UserSettingsProps {
  isCollapsed: boolean;
  onMenuChange?: (menu: SideMenuOptions) => void;
}
type LanguageCode = "en" | "es" | "no";

const languageCodeFlags: Record<LanguageCode, string> = {
  en: "🇬🇧",
  // es with chile flag
  es: "🇨🇱",
  no: "🇳🇴",
};

const isLanguageCode = (v: string): v is LanguageCode =>
  v === "en" || v === "es" || v === "no";

export const UserSettings: FC<UserSettingsProps> = ({
  isCollapsed,
  onMenuChange,
}) => {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useUser();
  const host = useShellHost();
  const [selectedLanguage, setSelectedLanguageState] = useState<LanguageCode>(
    () => {
      const cur = host.handle.i18n.language.get();
      return isLanguageCode(cur) ? cur : "en";
    },
  );

  useEffect(() => {
    return host.handle.i18n.language.subscribe(() => {
      const cur = host.handle.i18n.language.get();
      if (isLanguageCode(cur) && cur !== selectedLanguage) {
        setSelectedLanguageState(cur);
        try {
          if (i18n.language !== cur) void i18n.changeLanguage?.(cur);
        } catch {
          /* ignore */
        }
      }
    });
  }, [host, i18n, selectedLanguage]);

  const handleLanguageChange = (language: LanguageCode) => {
    host.handle.i18n.setLanguage(language);
    setSelectedLanguageState(language);
    try {
      void i18n.changeLanguage?.(language);
    } catch {
      /* shell may not have i18next initialized — store-only is enough for MFE bridge */
    }
  };

  const [logoutUser] = useLogoutUserMutation({
    onCompleted: () => {
      signOut();
    },
  });

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useOnClickOutside<HTMLDivElement>(() => setLangOpen(false));

  const langOptions: { code: LanguageCode; label: string }[] = [
    { code: "en", label: `${languageCodeFlags.en} ${t("en")}` },
    { code: "es", label: `${languageCodeFlags.es} ${t("es")}` },
    { code: "no", label: `${languageCodeFlags.no} ${t("no")}` },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Content
        horizontalAlign={isCollapsed ? "center" : "left"}
        className={style({ fontSize: rem(1.1) })}
        style={{
          marginBottom: px(14),
          marginLeft: isCollapsed ? 0 : rem(1.15),
        }}
      >
        <div
          ref={langRef}
          className={style({ position: "relative", display: "inline-block" })}
          data-testid="lang-switcher"
        >
          <Horizontal
            spacing={rem(0.1)}
            className={style({ cursor: "pointer" })}
            onClick={() => setLangOpen((v) => !v)}
            data-testid="lang-switcher-trigger"
          >
            <Content>{languageCodeFlags[selectedLanguage]}</Content>
            {!isCollapsed && (
              <Content
                padding={[0, rem(0.5)]}
                className={style({ fontSize: rem(1) })}
              >
                {t(selectedLanguage)}
              </Content>
            )}
          </Horizontal>
          {langOpen && (
            <div
              data-testid="lang-switcher-menu"
              className={style({
                position: "absolute",
                bottom: "100%",
                left: 0,
                marginBottom: 4,
                background: Styles.Colours.darkerBlue,
                border: `1px solid ${Styles.Colours.textDisabled}`,
                borderRadius: 4,
                boxShadow: Styles.boxShadow,
                zIndex: 1000,
                minWidth: 120,
                overflow: "hidden",
              })}
            >
              {langOptions.map((o) => (
                <div
                  key={o.code}
                  data-testid={`lang-switcher-option-${o.code}`}
                  onClick={() => {
                    handleLanguageChange(o.code);
                    setLangOpen(false);
                  }}
                  className={style({
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: rem(1),
                    color: Styles.Colours.textLight,
                    whiteSpace: "nowrap",
                    $nest: {
                      "&:hover": { background: Styles.Colours.darkestBlue },
                    },
                  })}
                >
                  {o.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </Content>
      <SidebarItem
        hideTextWhenCollapsed
        isCollapsed={isCollapsed}
        icon={faCog}
        text={t("Admin Configuration")}
        onClick={() => {
          onMenuChange && onMenuChange("config");
        }}
        permissionName={"ADMIN_PAGE"}
        data-testid="sidebar-item-admin-configuration"
      />
      <SidebarItem
        hideTextWhenCollapsed
        isCollapsed={isCollapsed}
        icon={faSignOutAlt}
        text={t("Sign out")}
        onClick={() => {
          logoutUser({
            variables: {
              email: user?.email ?? "",
            },
          });
        }}
        data-testid="sidebar-item-sign-out"
      />
    </div>
  );
};
